<template>
  <div>
    <v-card class="card-shadow border-radius-xl">
      <v-card-title class="d-flex align-center">
        <span class="font-weight-bold text-h5 text-typo">月工時</span>
        <v-spacer></v-spacer>
        <v-btn small text color="primary" :loading="loading" @click="fetchData">
          <v-icon left small>mdi-refresh</v-icon>
          重新整理
        </v-btn>
      </v-card-title>
      <v-card-subtitle>
        <v-row>
          <v-col cols="12" md="2">
            <v-select
              v-model="selectedYear"
              :items="yearOptions"
              hide-details
              dense
              outlined
              class="
                mt-0
                input-style
                font-size-input
                text-light-input
                placeholder-light
                input-icon
              "
              label="年份"
              @change="onFilterChange"
            ></v-select>
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="selectedMonth"
              :items="monthOptions"
              hide-details
              dense
              outlined
              class="
                mt-0
                input-style
                font-size-input
                text-light-input
                placeholder-light
                input-icon
              "
              label="月份"
              @change="onFilterChange"
            ></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="userId"
              label="User ID (選填)"
              dense
              outlined
              hide-details
              class="input-style"
              @keyup.enter="onFilterChange"
            />
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center">
            <v-btn
              color="primary"
              class="px-6 bg-gradient-primary"
              :loading="loading"
              @click="onFilterChange"
            >
              <v-icon left small>mdi-magnify</v-icon>
              查詢
            </v-btn>
          </v-col>
        </v-row>
        <v-alert v-if="error" type="error" dense text class="mt-2">
          {{ error }}
        </v-alert>
      </v-card-subtitle>
      <v-card-text class="px-0 py-0">
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          :server-items-length="totalItems"
          :items-per-page="itemsPerPage"
          :page.sync="page"
          class="table"
          mobile-breakpoint="0"
          hide-default-footer
          @update:page="onPageChange"
        >
          <template v-slot:item="{ item }">
            <tr>
              <td>{{ getUserName(item) }}</td>
              <td v-for="d in dayNumbers" :key="`d-${d}`">
                {{ getDayHours(item, d) }}
              </td>
              <td>{{ formatHours(item.total_work_hours) }}</td>
              <td>{{ item.total_overtime_minutes || 0 }}</td>
              <td>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      text
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click="showDetail(item)"
                    >
                      <v-icon left small>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>顯示詳細</span>
                </v-tooltip>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions>
        <v-row class="w-100">
          <v-col cols="6" class="ml-auto d-flex justify-end py-0">
            <v-pagination
              prev-icon="fa fa-angle-left"
              next-icon="fa fa-angle-right"
              class="pagination"
              color="#cb0c9f"
              v-model="page"
              :length="pageCount"
              @input="onPageChange"
              circle
            ></v-pagination>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="detailDialog" max-width="900px">
      <v-card>
        <v-card-title class="d-flex align-center">
          <div>
            <div class="font-weight-bold">月工時明細 - {{ detailTitle }}</div>
            <div class="text-body-2">
              總工時:
              {{ formatHours(selected ? selected.total_work_hours : "") }}
              小時， 加班:
              {{ selected ? selected.total_overtime_minutes || 0 : 0 }} 分鐘
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-simple-table dense fixed-header height="420px">
            <thead>
              <tr>
                <th class="text-left">日</th>
                <th class="text-left">類型</th>
                <th class="text-left">狀態</th>
                <th class="text-left">上班</th>
                <th class="text-left">下班</th>
                <th class="text-left">午休起</th>
                <th class="text-left">午休迄</th>
                <th class="text-left">休息起</th>
                <th class="text-left">休息迄</th>
                <th class="text-left">工時(h)</th>
                <th class="text-left">加班(分)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="slot in selected && selected.daily_slots
                  ? selected.daily_slots
                  : []"
                :key="slot.day"
              >
                <td>{{ slot.day }}</td>
                <td>{{ slot.dayType }}</td>
                <td>{{ slot.status }}</td>
                <td>{{ slot.on || "" }}</td>
                <td>{{ slot.off || "" }}</td>
                <td>{{ slot.lunchStart || "" }}</td>
                <td>{{ slot.lunchEnd || "" }}</td>
                <td>{{ slot.rest1Start || "" }}</td>
                <td>{{ slot.rest1End || "" }}</td>
                <td>{{ formatHours(slot.workHours) }}</td>
                <td>{{ slot.overtimeMinutes || 0 }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="detailDialog = false">關閉</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "MonthlyWorkHoursPage",
  data() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    return {
      loading: false,
      error: "",
      items: [],
      totalItems: 0,
      page: 1,
      pageCount: 1,
      itemsPerPage: 15,
      selectedYear: currentYear,
      selectedMonth: currentMonth,
      yearOptions: Array.from({ length: 10 }, (_, i) => currentYear - 5 + i),
      monthOptions: Array.from({ length: 12 }, (_, i) => i + 1),
      userId: "",
      detailDialog: false,
      selected: null,
      // headers computed
    };
  },
  computed: {
    ...mapGetters("company", ["selectedCompany"]),
    detailTitle() {
      if (!this.selected) return "";
      return `${this.selected.year_month} / User ${this.selected.user_id}`;
    },
    company_id() {
      return this.selectedCompany ? this.selectedCompany.id : null;
    },
    yearMonth() {
      const year = this.selectedYear;
      const month = String(this.selectedMonth).padStart(2, "0");
      return `${year}-${month}`;
    },
    dayNumbers() {
      return Array.from({ length: 31 }, (_, i) => i + 1);
    },
    headers() {
      const base = [{ text: "姓名", value: "user_name", width: "100px" }];
      const dayHeaders = this.dayNumbers.map((d) => ({
        text: `${d}`,
        value: `day_${d}`,
        sortable: false,
        width: "40px",
      }));
      const tail = [
        { text: "總工時(h)", value: "total_work_hours" },
        { text: "加班(分)", value: "total_overtime_minutes" },
        { text: "操作", value: "actions", sortable: false },
      ];
      return [...base, ...dayHeaders, ...tail];
    },
  },
  watch: {
    company_id: {
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.error = "";
          this.fetchData();
        }
      },
      immediate: true,
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    formatHours(val) {
      const n = Number(val);
      if (!Number.isFinite(n)) return "";
      return n.toFixed(2);
    },
    onFilterChange() {
      this.page = 1;
      this.fetchData();
    },
    onPageChange(newPage) {
      this.page = newPage;
      this.fetchData();
    },
    async fetchData() {
      if (!this.company_id) {
        this.error = "請先選擇公司";
        return;
      }
      if (!this.selectedYear || !this.selectedMonth) {
        this.error = "請先選擇年月";
        return;
      }
      this.loading = true;
      this.error = "";
      try {
        const params = {
          page: this.page,
          pageSize: this.itemsPerPage,
          year_month: this.yearMonth,
          company_id: this.company_id,
        };
        if (this.userId) params.user_id = this.userId;

        const { data } = await this.$axios.get("/api/monthly-work-hours", {
          params,
        });
        this.items = data?.data || [];
        const pagination = data?.meta?.pagination || {};
        this.totalItems = pagination.totalItems || 0;
        this.pageCount =
          pagination.totalPages ||
          Math.ceil(this.totalItems / this.itemsPerPage) ||
          1;
        if (pagination.currentPage) this.page = pagination.currentPage;
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          this.error = "沒有權限存取月工時資料";
        } else {
          this.error = err?.response?.data?.message || "載入失敗";
        }
      } finally {
        this.loading = false;
      }
    },
    showDetail(item) {
      this.selected = item;
      this.detailDialog = true;
    },
    getUserName(item) {
      if (!item) return "-";
      if (item.user && item.user.name) return item.user.name;
      if (item.user_name) return item.user_name;
      if (item.user_id) return item.user_id;
      return "-";
    },
    getDayHours(item, dayNum) {
      if (!item || !Array.isArray(item.daily_slots)) return "";
      const slot = item.daily_slots.find(
        (s) => Number(s.day) === Number(dayNum),
      );
      return slot ? this.formatHours(slot.workHours) : "";
    },
  },
};
</script>

<style scoped>
.table {
  border-radius: 0;
}
</style>
